/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UmssSelectComponent, SelectOption } from '../select/select.component';
import {
  MessageTypeEnum,
  MESSAGE_TYPE_ENUM_RECORD
} from '@umss/shared/data-access';

@Component({
  selector: 'umss-message-type-select',
  standalone: true,
  imports: [CommonModule, UmssSelectComponent],
  template: `
    <umss-select
      [options]="options"
      [placeholder]="placeholder"
      [label]="showLabel ? label : undefined"
      [helperText]="helperText"
      [errorMessage]="errorMessage"
      [size]="size"
      [disabled]="disabled"
      [required]="required"
      [error]="error"
      [loading]="{ selector: loading }"
      [multiple]="multiple"
      [hideSelectedOptions]="hideSelectedOptions"
      [(value)]="value"
      (selectionChange)="onSelectionChange($event)"
      (onFocus)="onFocus()"
      (onBlur)="onBlur()"
    />
  `,
  styles: `
    :host {
      display: contents;
    }
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssMessageTypeSelectComponent),
      multi: true
    }
  ]
})
export class UmssMessageTypeSelectComponent
  implements ControlValueAccessor, OnInit
{
  @Input() showLabel = false;
  @Input() label = 'Message Type';
  @Input() placeholder = 'Select a message type';
  @Input() helperText?: string;
  @Input() errorMessage?: string;
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';
  @Input() disabled = false;
  @Input() required = false;
  @Input() error = false;
  @Input() loading = false;
  @Input() multiple = false;
  @Input() hideSelectedOptions = false;

  @Input() set value(val: MessageTypeEnum | MessageTypeEnum[] | null) {
    this._value = val;
  }
  get value(): MessageTypeEnum | MessageTypeEnum[] | null {
    return this._value;
  }

  @Output() valueChange = new EventEmitter<
    MessageTypeEnum | MessageTypeEnum[] | null
  >();
  @Output() selectionChange = new EventEmitter<
    MessageTypeEnum | MessageTypeEnum[] | null
  >();

  options: SelectOption[] = [];
  private _value: MessageTypeEnum | MessageTypeEnum[] | null = null;

  private _onChange = (
    _value: MessageTypeEnum | MessageTypeEnum[] | null
  ): void => {
    // This is intentional - will be replaced by Angular forms
  };
  private _onTouched = (): void => {
    // This is intentional - will be replaced by Angular forms
  };

  ngOnInit(): void {
    this.initializeOptions();
  }

  private initializeOptions(): void {
    this.options = Object.entries(MESSAGE_TYPE_ENUM_RECORD).map(
      ([enumValue, label]) => ({
        value: enumValue as unknown as MessageTypeEnum,
        label: label
      })
    );
  }

  /** ControlValueAccessor implementation */
  writeValue(value: MessageTypeEnum | MessageTypeEnum[] | null): void {
    this._value = value;
  }

  registerOnChange(
    fn: (value: MessageTypeEnum | MessageTypeEnum[] | null) => void
  ): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Event handlers */
  onSelectionChange(value: MessageTypeEnum | MessageTypeEnum[] | null): void {
    this._value = value;
    this._onChange(value);
    this._onTouched();
    this.valueChange.emit(value);
    this.selectionChange.emit(value);
  }

  onFocus(): void {
    // Can be used for additional logic if needed
  }

  onBlur(): void {
    this._onTouched();
  }
}
